import { Button, Form, Input, Tooltip, Upload, message as antMessage, Progress } from 'antd'
import { PaperClipOutlined, PictureOutlined, SendOutlined } from '@ant-design/icons'
import React, { memo, useCallback, useState } from 'react'
import fetchAPI from '../../../../lib/utils/fetch-api'
import { ReplyBoxStyled } from './styles'
import { IReplyBoxProps } from '../model/types'
import type { RcFile } from 'antd/es/upload'

/** Преобразует ошибки API Avito в понятные пользователю сообщения */
function getAvitoErrorMessage(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error)
  if (msg.includes('Chat not found')) return 'Чат не найден. Обновите страницу.'
  if (msg.includes('Failed to send image'))
    return 'Не удалось отправить изображение в Avito. Попробуйте позже.'
  if (msg.includes('Failed to send message'))
    return 'Не удалось отправить сообщение в Avito. Попробуйте позже.'
  if (msg.includes('Something went wrong'))
    return 'Произошла ошибка при отправке. Попробуйте позже.'
  if (msg.includes('Either msg or imageId'))
    return 'Ошибка: необходимо ввести сообщение или прикрепить изображение.'
  if (
    msg.includes('Failed to fetch') ||
    msg.includes('NetworkError') ||
    msg.includes('Network request failed')
  )
    return 'Нет подключения к интернету. Проверьте соединение и попробуйте снова.'
  return msg || 'Произошла неизвестная ошибка. Попробуйте позже.'
}

interface UploadProgress {
  status: 'uploading' | 'error' | 'success'
  percent: number
  errorMessage?: string
}

export const ReplyBox: React.FC<IReplyBoxProps> = memo((props: IReplyBoxProps) => {
  const { id, chat_id, card_type } = props
  const [form] = Form.useForm()
  const [isBusy, setIsBusy] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<Record<string, UploadProgress>>({})

  // Загрузка и отправка одного изображения
  const uploadAndSendSingleImage = useCallback(
    async (file: RcFile) => {
      const fileName = file.name
      const formData = new FormData()
      formData.append('image', file)

      try {
        // Шаг 1: Загрузка в Avito
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { status: 'uploading', percent: 0 },
        }))

        const uploadResponse = await fetch(
          `/api/avito/chat/upload-image/${card_type}/${id}/${chat_id}`,
          {
            method: 'POST',
            body: formData,
          },
        )

        const uploadResult = await uploadResponse.json()

        if (!uploadResult.success) {
          setUploadProgress(prev => ({
            ...prev,
            [fileName]: { status: 'error', percent: 0, errorMessage: uploadResult.error },
          }))
          antMessage.error(
            getAvitoErrorMessage(new Error(uploadResult.error || 'Upload failed')),
            5,
          )
          return {
            success: false,
            error: uploadResult.error || 'Upload failed',
            filename: fileName,
          }
        }

        // Шаг 2: Отправка в чат
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { status: 'uploading', percent: 50 },
        }))

        const simulateError = new URLSearchParams(window.location.search).get('test_avito_error')
        const sendBody: Record<string, string> = { msg: '', imageId: uploadResult.imageId }
        if (simulateError && ['400', '404', '500'].includes(simulateError)) {
          sendBody.simulate_error = simulateError
        }

        const sendResult = await fetchAPI(`/api/avito/chat/${card_type}/${id}/${chat_id}`, {
          method: 'POST',
          body: JSON.stringify(sendBody),
        })

        if (sendResult?.error || sendResult?.errors) {
          const errMsg = Array.isArray(sendResult.errors)
            ? sendResult.errors
                .map((e: { msg?: string }) => e.msg)
                .filter(Boolean)
                .join(', ') || 'Ошибка отправки'
            : sendResult.error
          setUploadProgress(prev => ({
            ...prev,
            [fileName]: {
              status: 'error',
              percent: 50,
              errorMessage: getAvitoErrorMessage(new Error(errMsg)),
            },
          }))
          antMessage.error(getAvitoErrorMessage(new Error(errMsg)), 5)
          return {
            success: false,
            error: errMsg,
            filename: fileName,
          }
        }

        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { status: 'success', percent: 100 },
        }))

        return { success: true, imageId: uploadResult.imageId, filename: fileName }
      } catch (error) {
        setUploadProgress(prev => ({
          ...prev,
          [fileName]: { status: 'error', percent: 50, errorMessage: getAvitoErrorMessage(error) },
        }))
        antMessage.error(getAvitoErrorMessage(error), 5)
        return {
          success: false,
          error: (error as Error)?.message || 'Network error',
          filename: fileName,
        }
      }
    },
    [id, chat_id, card_type],
  )

  // Автоматическая отправка изображений
  const handleImageSelect = useCallback(
    async (file: RcFile) => {
      const result = await uploadAndSendSingleImage(file)

      if (result.success) {
        antMessage.success(`${result.filename} отправлено`)
        // Удаляем из списка после успешной отправки
        setTimeout(() => {
          setUploadProgress(prev => {
            const newProgress = { ...prev }
            delete newProgress[result.filename]
            return newProgress
          })
        }, 2000)
      }

      return result
    },
    [uploadAndSendSingleImage],
  )

  // Отправка текстового сообщения
  const sendMessage = useCallback(async () => {
    const value = form.getFieldValue('message')

    if (!value?.trim() || !chat_id) {
      return
    }

    const simulateError = new URLSearchParams(window.location.search).get('test_avito_error')

    setIsBusy(true)
    try {
      const body: Record<string, string> = { msg: value.trim() }
      if (simulateError && ['400', '404', '500'].includes(simulateError)) {
        body.simulate_error = simulateError
      }

      const result = await fetchAPI(`/api/avito/chat/${card_type}/${id}/${chat_id}`, {
        method: 'POST',
        body: JSON.stringify(body),
      })

      // fetchAPI при 400 возвращает result с error/errors, не бросает
      if (result?.error || result?.errors) {
        const errMsg = Array.isArray(result.errors)
          ? result.errors
              .map((e: { msg?: string }) => e.msg)
              .filter(Boolean)
              .join(', ') || 'Ошибка валидации'
          : result.error
        antMessage.error(getAvitoErrorMessage(new Error(errMsg)), 5)
        return
      }

      form.setFieldsValue({ message: '' })
      antMessage.success('Сообщение отправлено')
    } catch (error) {
      console.error('Error sending message:', error)
      antMessage.error(getAvitoErrorMessage(error), 5)
      // Сообщение остаётся в поле — пользователь может повторить отправку
    } finally {
      setIsBusy(false)
    }
  }, [id, form, chat_id, card_type])

  const [disabled, setDisabled] = useState<boolean>(true)
  const changeMessage = useCallback(() => {
    setDisabled(form.getFieldValue('message').trim()?.length === 0)
  }, [form])

  return (
    <ReplyBoxStyled>
      <Form form={form}>
        {/* Прогресс загрузки изображений */}
        {Object.keys(uploadProgress).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {Object.entries(uploadProgress).map(([filename, progress]) => {
              const progressStatus =
                progress.status === 'uploading'
                  ? 'active'
                  : progress.status === 'error'
                  ? 'exception'
                  : 'success'

              return (
                <Tooltip
                  key={filename}
                  title={
                    progress.status === 'error' && progress.errorMessage
                      ? progress.errorMessage
                      : undefined
                  }
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginBottom: 8,
                      padding: '8px 12px',
                      background: progress.status === 'error' ? '#fff2f0' : '#f5f5f5',
                      borderRadius: 6,
                    }}
                  >
                    <PictureOutlined
                      style={{
                        fontSize: 16,
                        color: progress.status === 'error' ? '#ff4d4f' : '#1890ff',
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 12,
                          marginBottom: 4,
                          color: progress.status === 'error' ? '#cf1322' : '#666',
                        }}
                      >
                        {filename}
                        {progress.status === 'error' && ' — ошибка'}
                      </div>
                      <Progress
                        percent={progress.percent}
                        size="small"
                        status={progressStatus}
                        showInfo={false}
                      />
                    </div>
                  </div>
                </Tooltip>
              )
            })}
          </div>
        )}

        {/* Компактный горизонтальный layout */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {/* Текстовое поле */}
          <Form.Item name="message" style={{ flex: 1, margin: 0 }}>
            <Input.TextArea
              rows={3}
              disabled={isBusy}
              placeholder="Введите сообщение..."
              onChange={changeMessage}
              style={{ resize: 'none' }}
              onPressEnter={e => {
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault()
                  sendMessage()
                }
              }}
              autoSize={{ minRows: 3, maxRows: 7 }}
            />
          </Form.Item>

          {/* Кнопка отправки справа */}
          <div style={{ display: 'flex', gap: 4, flexDirection: 'column' }}>
            {/* Загрузка изображений */}
            <div>
              <Upload
                beforeUpload={file => {
                  const isImage = file.type.startsWith('image/')
                  if (!isImage) {
                    antMessage.error('Можно загружать только изображения!')
                    return false
                  }
                  const isLt10M = file.size / 1024 / 1024 < 10
                  if (!isLt10M) {
                    antMessage.error(`${file.name} больше 10MB!`)
                    return false
                  }

                  handleImageSelect(file)
                  return false
                }}
                multiple
                maxCount={10}
                accept="image/*"
                disabled={isBusy}
                showUploadList={false}
              >
                <Button
                  type="default"
                  icon={<PaperClipOutlined />}
                  disabled={isBusy}
                  title="Изображение"
                >
                  Изображение
                </Button>
              </Upload>
            </div>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={sendMessage}
              disabled={isBusy || disabled}
              loading={isBusy}
              title="Отправить (Ctrl+Enter)"
            >
              Отправить
            </Button>
          </div>
        </div>
      </Form>
    </ReplyBoxStyled>
  )
})

ReplyBox.displayName = 'ReplyBox'
